import { Group, ThemeIcon, Tooltip } from '@mantine/core';
import { formatHumanName } from '@medplum/core';
import { HumanName } from '@medplum/fhirtypes';
import { ReactElement } from 'react';
import { CommunicationDirection } from '@bariendo/lib/constants';
import { formatGivenFamilyName } from '@bariendo/lib/human-name';
import { isServiceAccount } from '@bariendo/lib/profile';
import { SmsTimelineGroup, TimelineSmsCommunication } from '../types';
import { ActorAvatar } from './ActorAvatar';
import { TEAMMATE_INK, TEAMMATE_TINT } from '@/components/TeamAvatar';

/** How many faces fit before the rest become a count. */
const MAX_FACES = 3;

export type SmsThreadOwners = { kind: 'assignees' | 'lastReplier' | 'automated'; names: string[] } | undefined;

/** Names from the Task owner's `display` — resolving the reference would fetch. */
function assigneeNamesOf(item: TimelineSmsCommunication): string[] {
  const names = (item.resource.assigneeTask ?? []).map((task) => {
    const owner = task?.owner;
    const resource = owner?.resource as { name?: HumanName[] } | undefined;
    return owner?.display || (resource?.name?.[0] ? formatHumanName(resource.name[0]) : undefined);
  });

  return [...new Set(names.filter((name): name is string => Boolean(name)))];
}

/**
 * Whose thread is this? Everyone assigned to its newest assigned text, else
 * whoever spoke for us last.
 *
 * Plural because assignment is a fan-out: the stage's coordinator, everyone
 * who texted this patient in the last 72 hours, anyone named in the body.
 *
 * REPORTS ownership, never changes it — assignment is per-Communication and an
 * SMS card is a client-side grouping with no entity behind it, so a
 * thread-level control would have nothing to act on. Reassignment lives on the
 * message rows.
 */
export function resolveSmsThreadOwners(group: SmsTimelineGroup): SmsThreadOwners {
  // Groups are newest-first (`sortByTimestampDesc`). An owner-less Task does
  // not count: it survives being unassigned, and names nobody.
  for (const item of group.items) {
    const names = assigneeNamesOf(item);
    if (names.length > 0) {
      return { kind: 'assignees', names };
    }
  }

  // Only an OUTGOING message names a teammate — an inbound text's sender is
  // the patient.
  const lastItem = group.items[0];
  if (lastItem?.direction !== CommunicationDirection.OUTGOING) {
    return undefined;
  }

  const sender = lastItem.sender as { name?: HumanName[]; identifier?: Array<{ system?: string }> } | undefined;
  // `formatGivenFamilyName`, as the queue cells use: a middle name would
  // otherwise shift the initials TeammateAvatar derives from the string.
  const name = formatGivenFamilyName(sender?.name?.[0]);

  // A reminder is sent by a service account, not a colleague (BAR-2864).
  // Initials here would read as a teammate having replied. Checked before the
  // name guard: an account with no name still gets the system marker, as the
  // patient list gives it.
  if (isServiceAccount(sender)) {
    return { kind: 'automated', names: name ? [name] : [] };
  }

  return name ? { kind: 'lastReplier', names: [name] } : undefined;
}

/** The system marker's tooltip for an SMS: the automation's name when it has one. */
export function automatedSenderLabel(name: string | undefined): string {
  return name ? `Sent by ${name}` : 'Automated SMS';
}

/** Overlapping faces, then a named "+N" — a pooled thread can assign everyone
 * holding the role, and eight faces would push the timestamp off the row. */
function AvatarStack({ names, label }: { names: string[]; label: (name: string) => string }): ReactElement {
  const shown = names.slice(0, MAX_FACES);
  const overflow = names.slice(MAX_FACES);

  return (
    <Group gap={0} wrap="nowrap">
      {shown.map((name, index) => (
        <div key={name} style={{ marginLeft: index === 0 ? 0 : -8, display: 'inline-flex' }}>
          <ActorAvatar variant="teammate" label={label(name)} name={name} />
        </div>
      ))}
      {overflow.length > 0 && (
        <Tooltip label={overflow.join(', ')} position="top" withArrow>
          <ThemeIcon
            variant="filled"
            radius="xl"
            size={24}
            styles={{ root: { backgroundColor: TEAMMATE_TINT, color: TEAMMATE_INK } }}
            aria-label={`${overflow.length} more: ${overflow.join(', ')}`}
            role="img"
            style={{ marginLeft: -8 }}
          >
            <span style={{ fontSize: 10, fontWeight: 600 }}>+{overflow.length}</span>
          </ThemeIcon>
        </Tooltip>
      )}
    </Group>
  );
}

/** Display only. Every input is already in the timeline payload. */
export function SmsThreadAvatar({ group }: { group: SmsTimelineGroup }): ReactElement | undefined {
  const owners = resolveSmsThreadOwners(group);

  if (!owners) {
    return undefined;
  }

  if (owners.kind === 'automated') {
    return <ActorAvatar variant="system" label={automatedSenderLabel(owners.names[0])} />;
  }

  return (
    <AvatarStack
      names={owners.names}
      label={(name) => (owners.kind === 'assignees' ? `Assigned to ${name}` : `Last replied by ${name}`)}
    />
  );
}
